import { useState, useEffect, useRef, createContext, useContext, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
type AuthContextType = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  initializing: boolean;
  signingOut: boolean;
  signInWithGoogle: (returnUrl?: string) => Promise<void>;
  signInWithMicrosoft: (returnUrl?: string) => Promise<void>;
  signOut: () => Promise<void>;
};
const AuthContext = createContext<AuthContextType | undefined>(undefined);
export function AuthProvider({
  children
}: {
  children: ReactNode;
}) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(true);
  const [signingOut, setSigningOut] = useState(false);
  const queryClient = useQueryClient();

  // Ref guards to prevent race conditions between intentional actions
  // and the onAuthStateChange listener.
  const signingOutRef = useRef(false);
  const signedInRef = useRef(false);
  useEffect(() => {
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange((event, session) => {
      // During intentional sign-out, skip all state updates.
      // The hard redirect (window.location.href) will handle cleanup.
      if (signingOutRef.current) return;
      if (event as string === 'TOKEN_REFRESH_FAILED' || event === 'SIGNED_OUT' && !session) {
        setSession(null);
        setUser(null);
        queryClient.clear();
        localStorage.removeItem('pendingShareToken');
        localStorage.removeItem('pendingShareMeta');
        localStorage.removeItem('pendingInvitationToken');
        localStorage.removeItem('pendingInvitationType');
        localStorage.removeItem('postOnboardingRedirect');
        sessionStorage.removeItem('onboardingComplete');
      } else {
        setSession(session);
        setUser(session?.user ?? null);
      }
      setLoading(false);

      // Fresh OAuth sign-in: token was just issued, no stale-session risk.
      // Mark it so initializeAuth() can skip the redundant getUser() call.
      // IMPORTANT: Only mark as fresh for true OAuth handoffs — cached session
      // restoration also fires SIGNED_IN but needs server validation.
      if (event === 'SIGNED_IN') {
        const isOAuthHandoff = sessionStorage.getItem('oauth_handoff') === '1';
        if (isOAuthHandoff) {
          signedInRef.current = true;
          sessionStorage.removeItem('oauth_handoff');
        }
        setInitializing(false);
      }
    });
    const initializeAuth = async () => {
      try {
        const {
          data: {
            session
          }
        } = await supabase.auth.getSession();

        // Detect fresh OAuth callback by checking URL params (not just pathname).
        // OAuth can land on /auth, /, or any path with code/token params.
        const params = new URLSearchParams(window.location.search);
        const hash = window.location.hash;
        const isFreshOAuthCallback = params.has('code') || params.has('access_token') || params.has('refresh_token') || hash.includes('access_token') || hash.includes('refresh_token');
        if (session && !signedInRef.current && !isFreshOAuthCallback) {
          // Cached session from a previous visit — validate server-side
          // to catch deleted/revoked users. Only runs on normal reloads,
          // NOT on fresh OAuth callbacks (where the token was just issued).
          const {
            data: {
              user
            },
            error: userError
          } = await supabase.auth.getUser();
          if (userError || !user) {
            console.warn('[useAuth] Server rejected cached session — clearing stale state');
            await supabase.auth.signOut({
              scope: 'local'
            });
            setSession(null);
            setUser(null);
            queryClient.clear();
            localStorage.removeItem('pendingShareToken');
            localStorage.removeItem('pendingShareMeta');
            localStorage.removeItem('pendingInvitationToken');
            localStorage.removeItem('pendingInvitationType');
            localStorage.removeItem('postOnboardingRedirect');
            sessionStorage.removeItem('onboardingComplete');
          }
        }
        // If signedInRef is true or isFreshOAuthCallback, the SIGNED_IN event
        // already set (or will set) user/session — no redundant getUser() call.
      } catch (err) {
        console.error('[useAuth] Initialization error:', err);
        setSession(null);
        setUser(null);
      } finally {
        setLoading(false);
        setInitializing(false);
      }
    };
    initializeAuth();
    return () => subscription.unsubscribe();
  }, []);
  const signInWithGoogle = async (returnUrl?: string) => {
    if (returnUrl) {
      console.log('[useAuth] Storing returnUrl before Google OAuth:', returnUrl);
      localStorage.setItem('authReturnUrl', returnUrl);
    }
    sessionStorage.setItem('oauth_handoff', '1');
    const {
      error
    } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth`
      }
    });
    if (error) {
      console.error('Error signing in:', error.message);
      toast.error("Sign in failed", {
        description: error.message
      });
      throw error;
    }
  };
  const signInWithMicrosoft = async (returnUrl?: string) => {
    if (returnUrl) {
      console.log('[useAuth] Storing returnUrl before Microsoft OAuth:', returnUrl);
      localStorage.setItem('authReturnUrl', returnUrl);
    }
    sessionStorage.setItem('oauth_handoff', '1');
    const {
      error
    } = await supabase.auth.signInWithOAuth({
      provider: 'azure',
      options: {
        redirectTo: `${window.location.origin}/auth`,
        scopes: 'email profile openid'
      }
    });
    if (error) {
      console.error('Error signing in with Microsoft:', error.message);
      toast.error("Sign in failed", {
        description: error.message
      });
      throw error;
    }
  };
  const signOut = async () => {
    // Set the ref FIRST — this tells the onAuthStateChange listener to
    // ignore the SIGNED_OUT event, preventing the AccessDenied flash.
    signingOutRef.current = true;
    setSigningOut(true);
    await queryClient.cancelQueries();
    const {
      error
    } = await supabase.auth.signOut();
    queryClient.clear();
    if (error) {
      if (error.message?.includes('Auth session missing')) {
        console.warn('Sign out called with missing session, clearing local state.');
        return;
      }

      // Reset flags on failure so the app can recover
      signingOutRef.current = false;
      setSigningOut(false);
      console.error('Error signing out:', error.message);
      toast.error('Sign out failed', {
        description: error.message
      });
      throw error;
    }

    // Note: The caller (AppHeader) performs window.location.href = '/'
    // which does a full page reload, giving us a clean AuthProvider state.
  };
  return <AuthContext.Provider value={{
    user,
    session,
    loading,
    initializing,
    signingOut,
    signInWithGoogle,
    signInWithMicrosoft,
    signOut
  }}>
      {children}
    </AuthContext.Provider>;
}
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}